/**
 * Supabase Client for Dashboard - Site-aware version with Realtime and Auth support
 */

class SupabaseClient {
    constructor(url, anonKey) {
        this.url = url;
        this.anonKey = anonKey;
        this._accessToken = null;
        this._refreshToken = null;
        this._user = null;
        this._sessionExpiresAt = null;
        this._refreshTimeout = null;
        this._onAuthStateChange = null;
        
        // Initialize headers with anon key
        this.headers = {
            'apikey': anonKey,
            'Authorization': `Bearer ${anonKey}`,
            'Content-Type': 'application/json',
            'Prefer': 'return=representation'
        };
        
        // Realtime state
        this._realtimeSocket = null;
        this._realtimeChannels = new Map();
        this._realtimeCallbacks = new Map();
        this._heartbeatInterval = null;
        this._reconnectTimeout = null;
        this._isConnected = false;
        this._messageRef = 0;
        
        // Try to restore session from localStorage
        this._restoreSession();
    }

    // ========================================
    // Auth Methods
    // ========================================

    /**
     * Set callback for auth state changes
     * @param {Function} callback - (event, session) => void
     */
    onAuthStateChange(callback) {
        this._onAuthStateChange = callback;
    }

    /**
     * Sign in with email and password
     * @param {string} email 
     * @param {string} password 
     * @returns {Promise<{user, session, error}>}
     */
    async signInWithPassword(email, password) {
        try {
            const response = await fetch(`${this.url}/auth/v1/token?grant_type=password`, {
                method: 'POST',
                headers: {
                    'apikey': this.anonKey,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, password })
            });

            if (!response.ok) {
                const error = await response.json();
                return { user: null, session: null, error: error.error_description || error.msg || 'Login failed' };
            }

            const data = await response.json();
            this._setSession(data);
            
            if (this._onAuthStateChange) {
                this._onAuthStateChange('SIGNED_IN', this.getSession());
            }

            return { user: this._user, session: this.getSession(), error: null };
        } catch (error) {
            return { user: null, session: null, error: error.message };
        }
    }

    /**
     * Sign up with email and password
     * @param {string} email 
     * @param {string} password 
     * @returns {Promise<{user, session, error}>}
     */
    async signUp(email, password) {
        try {
            const response = await fetch(`${this.url}/auth/v1/signup`, {
                method: 'POST',
                headers: {
                    'apikey': this.anonKey,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, password })
            });

            if (!response.ok) {
                const error = await response.json();
                return { user: null, session: null, error: error.error_description || error.msg || 'Signup failed' };
            }

            const data = await response.json();
            
            // If email confirmation is required, user won't have a session yet
            if (data.access_token) {
                this._setSession(data);
                if (this._onAuthStateChange) {
                    this._onAuthStateChange('SIGNED_IN', this.getSession());
                }
            }

            return { user: data.user || this._user, session: this.getSession(), error: null };
        } catch (error) {
            return { user: null, session: null, error: error.message };
        }
    }

    /**
     * Sign out current user
     */
    async signOut() {
        try {
            if (this._accessToken) {
                await fetch(`${this.url}/auth/v1/logout`, {
                    method: 'POST',
                    headers: {
                        'apikey': this.anonKey,
                        'Authorization': `Bearer ${this._accessToken}`,
                        'Content-Type': 'application/json'
                    }
                });
            }
        } catch (e) {
            console.warn('[Auth] Logout request failed:', e);
        }

        this._clearSession();
        
        if (this._onAuthStateChange) {
            this._onAuthStateChange('SIGNED_OUT', null);
        }
    }

    /**
     * Get current session
     * @returns {{access_token, refresh_token, expires_at, user}|null}
     */
    getSession() {
        if (!this._accessToken) return null;
        return {
            access_token: this._accessToken,
            refresh_token: this._refreshToken,
            expires_at: this._sessionExpiresAt,
            user: this._user
        };
    }

    /**
     * Get current user
     * @returns {Object|null}
     */
    getUser() {
        return this._user;
    }

    /**
     * Check if user is authenticated
     * @returns {boolean}
     */
    isAuthenticated() {
        return !!this._accessToken && !!this._user;
    }

    /**
     * Refresh the access token
     */
    async refreshSession() {
        if (!this._refreshToken) {
            console.log('[Auth] No refresh token available');
            return false;
        }

        try {
            const response = await fetch(`${this.url}/auth/v1/token?grant_type=refresh_token`, {
                method: 'POST',
                headers: {
                    'apikey': this.anonKey,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ refresh_token: this._refreshToken })
            });

            if (!response.ok) {
                console.error('[Auth] Failed to refresh token');
                this._clearSession();
                if (this._onAuthStateChange) {
                    this._onAuthStateChange('TOKEN_REFRESHED_ERROR', null);
                }
                return false;
            }

            const data = await response.json();
            this._setSession(data);
            console.log('[Auth] Session refreshed');
            
            if (this._onAuthStateChange) {
                this._onAuthStateChange('TOKEN_REFRESHED', this.getSession());
            }
            
            return true;
        } catch (error) {
            console.error('[Auth] Refresh error:', error);
            return false;
        }
    }

    /**
     * Set session from auth response
     * @private
     */
    _setSession(data) {
        this._accessToken = data.access_token;
        this._refreshToken = data.refresh_token;
        this._user = data.user;
        this._sessionExpiresAt = data.expires_at || (Date.now() / 1000 + data.expires_in);

        // Update headers with user token
        this.headers['Authorization'] = `Bearer ${this._accessToken}`;

        // Save to localStorage
        localStorage.setItem('supabase_session', JSON.stringify({
            access_token: this._accessToken,
            refresh_token: this._refreshToken,
            expires_at: this._sessionExpiresAt,
            user: this._user
        }));

        // Schedule token refresh (5 minutes before expiry)
        this._scheduleRefresh();
        
        console.log('[Auth] Session set for user:', this._user?.email);
    }

    /**
     * Clear session
     * @private
     */
    _clearSession() {
        this._accessToken = null;
        this._refreshToken = null;
        this._user = null;
        this._sessionExpiresAt = null;

        // Reset headers to anon key
        this.headers['Authorization'] = `Bearer ${this.anonKey}`;

        // Clear localStorage
        localStorage.removeItem('supabase_session');

        // Clear refresh timeout
        if (this._refreshTimeout) {
            clearTimeout(this._refreshTimeout);
            this._refreshTimeout = null;
        }
        
        console.log('[Auth] Session cleared');
    }

    /**
     * Restore session from localStorage
     * @private
     */
    _restoreSession() {
        try {
            const stored = localStorage.getItem('supabase_session');
            if (!stored) return;

            const session = JSON.parse(stored);
            
            // Check if session is expired
            const now = Date.now() / 1000;
            if (session.expires_at && session.expires_at < now) {
                // Session expired, try to refresh
                this._refreshToken = session.refresh_token;
                this.refreshSession();
                return;
            }

            // Restore valid session
            this._accessToken = session.access_token;
            this._refreshToken = session.refresh_token;
            this._sessionExpiresAt = session.expires_at;
            this._user = session.user;
            
            // Update headers
            this.headers['Authorization'] = `Bearer ${this._accessToken}`;
            
            // Schedule refresh
            this._scheduleRefresh();
            
            console.log('[Auth] Session restored for user:', this._user?.email);
        } catch (e) {
            console.warn('[Auth] Failed to restore session:', e);
            localStorage.removeItem('supabase_session');
        }
    }

    /**
     * Schedule automatic token refresh
     * @private
     */
    _scheduleRefresh() {
        if (this._refreshTimeout) {
            clearTimeout(this._refreshTimeout);
        }

        if (!this._sessionExpiresAt) return;

        const now = Date.now() / 1000;
        const expiresIn = this._sessionExpiresAt - now;
        
        // Refresh 5 minutes before expiry, minimum 10 seconds
        const refreshIn = Math.max((expiresIn - 300) * 1000, 10000);

        this._refreshTimeout = setTimeout(() => {
            this.refreshSession();
        }, refreshIn);

        console.log(`[Auth] Token refresh scheduled in ${Math.round(refreshIn / 1000)}s`);
    }

    // ========================================
    // Realtime WebSocket Methods
    // ========================================

    /**
     * Initialize Realtime WebSocket connection
     */
    connectRealtime() {
        if (this._realtimeSocket && this._isConnected) {
            console.log('[Realtime] Already connected');
            return;
        }

        const wsUrl = this.url.replace('https://', 'wss://').replace('http://', 'ws://');
        const realtimeUrl = `${wsUrl}/realtime/v1/websocket?apikey=${this.anonKey}&vsn=1.0.0`;

        console.log('[Realtime] Connecting...');
        this._realtimeSocket = new WebSocket(realtimeUrl);

        this._realtimeSocket.onopen = () => {
            console.log('[Realtime] Connected');
            this._isConnected = true;
            this._startHeartbeat();
            
            // Rejoin existing channels after reconnect
            this._realtimeChannels.forEach((config, channelId) => {
                this._joinChannel(channelId, config);
            });
        };

        this._realtimeSocket.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                this._handleRealtimeMessage(message);
            } catch (e) {
                console.error('[Realtime] Failed to parse message:', e);
            }
        };

        this._realtimeSocket.onclose = (event) => {
            console.log('[Realtime] Disconnected:', event.code, event.reason);
            this._isConnected = false;
            this._stopHeartbeat();
            
            // Auto-reconnect after 3 seconds
            if (!this._reconnectTimeout) {
                this._reconnectTimeout = setTimeout(() => {
                    this._reconnectTimeout = null;
                    this.connectRealtime();
                }, 3000);
            }
        };

        this._realtimeSocket.onerror = (error) => {
            console.error('[Realtime] WebSocket error:', error);
        };
    }

    /**
     * Disconnect Realtime WebSocket
     */
    disconnectRealtime() {
        if (this._reconnectTimeout) {
            clearTimeout(this._reconnectTimeout);
            this._reconnectTimeout = null;
        }
        
        this._stopHeartbeat();
        
        if (this._realtimeSocket) {
            this._realtimeSocket.close();
            this._realtimeSocket = null;
        }
        
        this._isConnected = false;
        this._realtimeChannels.clear();
        this._realtimeCallbacks.clear();
        console.log('[Realtime] Disconnected manually');
    }

    /**
     * Start heartbeat to keep connection alive
     */
    _startHeartbeat() {
        this._stopHeartbeat();
        this._heartbeatInterval = setInterval(() => {
            if (this._realtimeSocket && this._isConnected) {
                this._sendMessage({
                    topic: 'phoenix',
                    event: 'heartbeat',
                    payload: {},
                    ref: String(++this._messageRef)
                });
            }
        }, 30000);
    }

    /**
     * Stop heartbeat
     */
    _stopHeartbeat() {
        if (this._heartbeatInterval) {
            clearInterval(this._heartbeatInterval);
            this._heartbeatInterval = null;
        }
    }

    /**
     * Send message through WebSocket
     */
    _sendMessage(message) {
        if (this._realtimeSocket && this._realtimeSocket.readyState === WebSocket.OPEN) {
            this._realtimeSocket.send(JSON.stringify(message));
        }
    }

    /**
     * Handle incoming Realtime messages
     */
    _handleRealtimeMessage(message) {
        const { topic, event, payload } = message;

        // Ignore system messages
        if (topic === 'phoenix' || event === 'phx_reply') {
            return;
        }

        // Handle postgres_changes events
        if (event === 'postgres_changes') {
            const callbacks = this._realtimeCallbacks.get(topic);
            if (callbacks) {
                callbacks.forEach(cb => {
                    try {
                        cb(payload);
                    } catch (e) {
                        console.error('[Realtime] Callback error:', e);
                    }
                });
            }
        }
    }

    /**
     * Join a channel for real-time updates
     */
    _joinChannel(channelId, config) {
        const joinMessage = {
            topic: channelId,
            event: 'phx_join',
            payload: {
                config: {
                    broadcast: { self: false },
                    postgres_changes: config.postgresChanges || []
                }
            },
            ref: String(++this._messageRef)
        };

        this._sendMessage(joinMessage);
        console.log(`[Realtime] Joined channel: ${channelId}`);
    }

    /**
     * Subscribe to table changes
     * @param {string} table - Table name to subscribe
     * @param {string} site - Site filter (optional)
     * @param {Function} callback - Callback for changes (payload) => void
     * @returns {string} Channel ID for unsubscribe
     */
    subscribeToTable(table, site, callback) {
        const channelId = `realtime:public:${table}${site ? `:${site}` : ''}`;
        
        // Build postgres_changes config
        const filter = site ? `site=eq.${site}` : undefined;
        const postgresChanges = [{
            event: '*',
            schema: 'public',
            table: table,
            filter: filter
        }];

        // Store channel config
        this._realtimeChannels.set(channelId, { postgresChanges });

        // Store callback
        if (!this._realtimeCallbacks.has(channelId)) {
            this._realtimeCallbacks.set(channelId, new Set());
        }
        this._realtimeCallbacks.get(channelId).add(callback);

        // Join if connected
        if (this._isConnected) {
            this._joinChannel(channelId, { postgresChanges });
        }

        return channelId;
    }

    /**
     * Unsubscribe from a channel
     * @param {string} channelId - Channel ID returned from subscribeToTable
     */
    unsubscribe(channelId) {
        if (this._realtimeChannels.has(channelId)) {
            // Send leave message
            this._sendMessage({
                topic: channelId,
                event: 'phx_leave',
                payload: {},
                ref: String(++this._messageRef)
            });

            this._realtimeChannels.delete(channelId);
            this._realtimeCallbacks.delete(channelId);
            console.log(`[Realtime] Unsubscribed from: ${channelId}`);
        }
    }

    /**
     * Pause all subscriptions (for visibility change)
     */
    pauseRealtime() {
        if (this._realtimeSocket && this._isConnected) {
            this._stopHeartbeat();
            console.log('[Realtime] Paused');
        }
    }

    /**
     * Resume all subscriptions
     */
    resumeRealtime() {
        if (this._realtimeSocket && this._isConnected) {
            this._startHeartbeat();
            console.log('[Realtime] Resumed');
        } else {
            this.connectRealtime();
        }
    }

    /**
     * Check if realtime is connected
     */
    isRealtimeConnected() {
        return this._isConnected;
    }

    /**
     * Call RPC function
     */
    async rpc(functionName, params = {}) {
        const response = await fetch(`${this.url}/rest/v1/rpc/${functionName}`, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify(params)
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'RPC call failed');
        }

        return await response.json();
    }

    /**
     * Query table with site filter support
     */
    async query(table, options = {}) {
        const params = new URLSearchParams();

        if (options.select) params.set('select', options.select);
        if (options.order) params.set('order', options.order);
        if (options.limit) params.set('limit', options.limit);
        if (options.offset) params.set('offset', options.offset);

        // Site filter (auto-inject if site is provided)
        if (options.site) {
            params.append('site', `eq.${options.site}`);
        }

        // Additional filters
        if (options.filters) {
            for (const [key, value] of Object.entries(options.filters)) {
                params.append(key, value);
            }
        }

        const response = await fetch(`${this.url}/rest/v1/${table}?${params}`, {
            method: 'GET',
            headers: this.headers
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Query failed');
        }

        return await response.json();
    }

    /**
     * Insert records (site is included in data)
     */
    async insert(table, data) {
        const response = await fetch(`${this.url}/rest/v1/${table}`, {
            method: 'POST',
            headers: this.headers,
            body: JSON.stringify(Array.isArray(data) ? data : [data])
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Insert failed');
        }

        return await response.json();
    }

    /**
     * Update records
     */
    async update(table, data, filters = {}) {
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(filters)) {
            params.append(key, value);
        }

        const response = await fetch(`${this.url}/rest/v1/${table}?${params}`, {
            method: 'PATCH',
            headers: this.headers,
            body: JSON.stringify(data)
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Update failed');
        }

        return await response.json();
    }

    /**
     * Delete records
     */
    async delete(table, filters = {}) {
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(filters)) {
            params.append(key, value);
        }

        const response = await fetch(`${this.url}/rest/v1/${table}?${params}`, {
            method: 'DELETE',
            headers: this.headers
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Delete failed');
        }

        return await response.json();
    }

    /**
     * Upload file to storage
     * @param {string} bucket - Bucket name
     * @param {string} path - Path including site prefix (e.g., 'genspark/reference/...')
     * @param {File} file - File to upload
     */
    async uploadFile(bucket, path, file) {
        const response = await fetch(`${this.url}/storage/v1/object/${bucket}/${path}`, {
            method: 'POST',
            headers: {
                'apikey': this.anonKey,
                'Authorization': `Bearer ${this.anonKey}`,
                'Content-Type': file.type,
                'x-upsert': 'true'
            },
            body: file
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Upload failed');
        }

        return await response.json();
    }

    /**
     * Get public URL for file
     */
    getPublicUrl(bucket, path) {
        return `${this.url}/storage/v1/object/public/${bucket}/${path}`;
    }

    /**
     * Get task stats (site-aware)
     * @param {string|null} site - Site ID or null for all sites
     */
    async getTaskStats(site = null) {
        return await this.rpc('get_task_stats', { p_site: site });
    }

    /**
     * Get all sites stats (for badges)
     */
    async getAllSitesStats() {
        // Get stats grouped by site
        return await this.rpc('get_task_stats', { p_site: null });
    }

    /**
     * Reset a single task back to pending
     */
    async resetTask(taskId, site) {
        return await this.rpc('reset_task', { p_task_id: taskId, p_site: site });
    }

    /**
     * Force recover all stuck tasks for a site
     */
    async forceRecoverStuckTasks(site) {
        return await this.rpc('force_recover_stuck_tasks', { p_site: site });
    }

    /**
     * Get task details for recovery (including agent ID)
     */
    async getTaskForRecovery(taskId, site) {
        return await this.rpc('get_task_for_recovery', { p_task_id: taskId, p_site: site });
    }

    /**
     * Count records with filters
     */
    async count(table, options = {}) {
        const params = new URLSearchParams();
        params.set('select', 'count');

        if (options.site) {
            params.append('site', `eq.${options.site}`);
        }

        if (options.filters) {
            for (const [key, value] of Object.entries(options.filters)) {
                params.append(key, value);
            }
        }

        const response = await fetch(`${this.url}/rest/v1/${table}?${params}`, {
            method: 'GET',
            headers: {
                ...this.headers,
                'Prefer': 'count=exact'
            }
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Count failed');
        }

        // Get count from content-range header
        const contentRange = response.headers.get('content-range');
        if (contentRange) {
            const match = contentRange.match(/\/(\d+)/);
            if (match) {
                return parseInt(match[1]);
            }
        }

        return 0;
    }
}

// Global instance
let supabase = null;

function initSupabase(url, anonKey) {
    supabase = new SupabaseClient(url, anonKey);
    return supabase;
}

function getSupabase() {
    return supabase;
}

// Export functions for use by other modules
export { SupabaseClient, initSupabase, getSupabase };
